import type { Messages } from '../messages.js'

/**
 * Finnish. Counts take the partitive singular, which is why the plural forms read `2 sanaa`
 * rather than `2 sanat`: `Intl.PluralRules` calls that `other` and it is not a plural at all.
 */
export const fi: Messages = {
  tag: 'fi',

  readingDictionary: 'Luetaan sanakirjaa…',
  noWordList: 'Ei sanalistaa kielelle ”{language}”. Rakenna se:  pnpm dictionary build',
  emptyWordList: 'Sanalista kielelle ”{language}” on tyhjä.',

  flips: 'siirrot',
  score: 'pisteet',
  words: 'sanat',
  round: 'kierros',
  ticksLeftLabel: 'Kierroksella jäljellä oleva aika',
  typeAWord: 'kirjoita sana',
  tapPrompt: 'kosketa ottaaksesi tai palauttaaksesi, sitten {action}',

  boardOfTiles: 'Pelilauta, {n} laattaa',
  faceDown: 'nurin',
  wildCard: 'jokeri',
  wildKey: 'mikä tahansa kirjain',
  letterReplaced: '{from} muuttui kirjaimeksi {to}',
  letterSwap: 'KIRJAIN VAIHTUI!',
  spentTile: 'käytetty laatta',
  hiddenWhilePaused: 'piilotettu tauon ajaksi',
  letterInWord: '{letter}, sanan {position}. kirjain',

  completeWord: 'Hyväksy sana',
  completeShort: 'Hyväksy',
  reset: 'Tyhjennä',
  pause: 'Tauko',
  resume: 'Jatka',
  newGame: 'Uusi peli',
  paused: 'Tauolla',
  outOfFlips: 'Siirrot loppuivat',
  finalResult: '{score} pistettä, {words} {rounds}',
  playAgain: 'Pelaa uudelleen',
  share: 'Jaa',
  shareCopied: 'Kopioitu.',
  shareSelect: 'Kopioi tämä:',

  lettersSelect: 'kirjaimet valitsevat',
  keysWild: 'otetaan, kun kirjoitat kirjaimen jota mikään laatta ei näytä',
  clearsEvery: 'poistaa kaikki valitut {letter}-kirjaimet',
  undoLastLetter: 'peruu viimeisen kirjaimen',
  noWordsYet: 'Ei vielä sanoja.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'jo löydetty',
  reasonTooShort: 'liian lyhyt',
  reasonNotAWord: 'ei ole sana',
  reasonAllFound: 'sinulla on ne jo kaikki',
  noSuchLetterUp: 'ei {letter}-kirjainta näkyvissä',
  nothingUp: 'ei mitään näkyvissä',
  letterHidden: 'kirjain piiloutui',
  shuffled: 'sekoitettu',
  shuffledAndBilled: 'sekoitettu, veloitettiin {flips} käyttämätöntä siirtoa',
  tooFewLetters: 'Liian vähän kirjaimia jäljellä',

  gameLanguage: 'kieli',
  interfaceLanguage: 'käyttöliittymä',
  dictionarySize: '{common} yleistä / {full} sanaa',
  filterLanguages: 'Hae kieliä',
  noMatches: 'Ei osumia',

  nerdMode: 'asiantuntijatila',
  rules: 'Säännöt',
  difficulty: 'vaikeustaso',
  difficultyNames: { easy: 'helppo', medium: 'normaali', hard: 'vaikea', insane: 'raaka' },
  pausedNoBoard: 'Tämä peli ei päädy tulostaululle.',
  about: 'Tietoja',
  themeLabel: 'teema',
  themeNames: { traditional: 'perinteinen', light: 'vaalea', contrast: 'suuri kontrasti' },
  tiles: 'laatat (N)',
  secondsPerTick: 'sekuntia / askel',
  holdTicks: 'pitoaskeleet',
  minWord: 'lyhin sana',
  startingFlips: 'siirrot alussa',
  wildChance: 'jokerin todennäköisyys',
  replaceChance: 'kirjaimen vaihtumisen todennäköisyys',
  hideChance: 'kirjaimen piiloutumisen todennäköisyys',
  wordCompleteMode: 'sana valmis',
  wordCompleteNames: { shuffle: 'sekoita', spend: 'kuluta', keep: 'säilytä' },
  flipEconomy: 'siirtotalous',
  flipEconomyNames: {
    none: 'ei mitään',
    perLetter: 'kirjainta kohti',
    fibonacci: 'fibonacci',
    overMinimum: 'yli minimin',
  },
  repeatedLetterKey: 'toistuvan kirjaimen näppäin',
  keySchemeNames: { cycle: 'kierrätä', advance: 'edistä' },
  keySchemeHelp: {
    cycle:
      'A ottaa seuraavan vapaan A:n, ja kun kaikki ovat sanassa, se poistaa ne. ' +
      'Vaihto+A poistaa ne myös.',
    advance: 'A ottaa seuraavan vapaan A:n. Vaihto+A poistaa sanasta kaikki A:t.',
  },

  whatThatMeans: 'Mitä se tarkoittaa',
  factRound: 'kierros',
  factWholeBoardUp: 'koko lauta näkyvissä',
  factRoundCosts: 'kierros kustantaa',
  factFlipsBuy: 'aloitussiirrot riittävät',
  factThisBoard: 'tämä lauta',
  factBoardHadToAdmit: 'laudan oli sallittava',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, pisin {longest}',
  wordsIncludingOneOf: '{words}, joista yksi {ceiling} kirjaimen',
  scorelessRounds: '{rounds} ilman pisteitä',

  whatAWordPays: 'Mitä sana tuottaa',
  columnLetters: 'kirjaimet',
  columnCost: 'hinta',
  columnPoints: 'pisteet',
  columnFlips: 'siirrot',
  columnNet: 'netto',

  canonicalRules: 'Viralliset {difficulty}-säännöt.',
  customRules: 'Muutettu esiasetuksesta. Omilla säännöillä saatuja tuloksia ei sijoiteta.',
  applyAndStart: 'Ota käyttöön ja aloita uusi peli',
  changesNextGame: 'Muutokset vaikuttavat seuraavaan peliin.',
  presets: 'Esiasetukset:',

  start: 'Aloita',
  restart: 'Aloita uudelleen',
  quit: 'Lopeta',
  quitTitle: 'Lopetetaanko tämä peli?',
  restartTitle: 'Aloitetaanko peli alusta?',
  restartConfirm: 'Alusta',
  quitConfirm: 'Lopeta',
  keepPlaying: 'Jatka pelaamista',
  personalBest: 'Parhaat pelisi',
  newPersonalBest: 'Uusi henkilökohtainen ennätys.',
  columnRank: '#',
  notRanked: 'Omat säännöt, joten tätä peliä ei sijoiteta.',
  notRankedPaused: 'Kello pysähtyi, joten tätä peliä ei sijoiteta.',
  rankOfTotal: '{rank} / {total}',

  howToPlay: 'Näin pelataan',

  backToGame: 'Takaisin peliin',
  welcomeTitle: 'Tervetuloa Blinkerediin',
  tutorialSkip: 'Ohita',
  tutorialNext: 'Seuraava',
  tutorialBack: 'Takaisin',
  tutorialStart: 'Aloita peli',
  tutorialHideAgain: 'Älä näytä uudelleen',
  tutorialProgress: '{n} / {total}',
  tutorialSkipTitle: 'Ohitetaanko esittely?',
  tutGoalTitle: 'Blinkered on kilpajuoksu',
  tutGoalBody:
    'Aloitat pelin kiinteällä määrällä siirtoja, ja jokainen kääntyvä laatta kuluttaa yhden siirron. Kun siirrot loppuvat, peli päättyy. Sanoista saa siirtoja, ja mitä pidempi sana, sitä enemmän siirtoja saat.',
  tutSpentBody:
    'Sanassa käyttämäsi kirjaimet kääntyvät nurin seuraavaan kierrokseen asti. Vain kirjaimen vaihtuminen muuttaa sitä, mitä kirjaimia laudalla on.',
  tutChangeTitle: 'Kirjaimet voivat muuttua',
  tutSameLetters:
    'Huomasitko sanan, jota et ehtinyt pelata? Samat kirjaimet palaavat seuraavalla kierroksella uusiin paikkoihin sekoitettuina.',
  tutPickLetters: 'Napauta haluamiasi kirjaimia järjestyksessä muodostaaksesi sanan.',
  tutMoreTurn:
    'Laatat kääntyvät edelleen sillä aikaa kun mietit, joten parempi kirjain voi olla vielä tulossa.',
  tutTapBack:
    'Napautitko väärää? Napauta sitä uudelleen ja saat sen takaisin. Mitä tahansa, ei vain viimeistä.',
  tutComplete: 'Paina Valmis, kun sana on kasassa.',
  htBoardTitle: 'Pelilauta',
  htBoardBody:
    'Laatat kääntyvät esiin yksi kerrallaan, satunnaisessa järjestyksessä. Näkyvistä laatoista muodostat sanoja.',
  htWordsTitle: 'Sanat',
  htWordsBody:
    'Muodosta sana näkyvistä laatoista kirjoittamalla tai napsauttamalla kirjaimet järjestyksessä.',
  htFlipsTitle: 'Siirrot',
  htFlipsBody:
    'Jokainen kääntyvä laatta kuluttaa yhden siirron. Valmis sana palauttaa siirtoja saldoosi, ja pitkät sanat palauttavat enemmän. Kun siirrot loppuvat, peli päättyy.',
  htRoundTitle: 'Kierros',
  htRoundBody:
    'Kun kierroksen viimeinen laatta kääntyy, koko lauta on hetken näkyvissä. Sitten laatat käännetään nurin ja sekoitetaan, ja uusi kierros alkaa.',
  htLanguagesTitle: 'Kielet',
  htLanguagesBody:
    '{n}. Jokainen lauta ratkeaa arkisilla sanoilla. Harvinainen sana kelpaa silti, jos sanakirja tuntee sen.',
  htKeysTitle: 'Näppäimistö',
  htWildTitle: 'Jokerit',
  htWildBody:
    'Joskus kirjaimen tilalle ilmestyy jokeri. Jokeri käy mistä tahansa kirjaimesta, joka muodostaa kelvollisen sanan. Jo tehty sana ei kelpaa.',
  htHideTitle: 'Piiloutuvat kirjaimet',
  htHideBody:
    'Vaikeammilla tasoilla esillä oleva kirjain voi kääntyä itsestään takaisin ja palata kierroksen aikana myöhemmin. Ajastin saa siitä yhden lisäpykälän, punaisella. Jo valitut kirjaimet eivät käänny takaisin.',
  tutHideBody:
    'Vaikeammilla tasoilla esillä oleva kirjain voi kääntyä itsestään takaisin ja palata kierroksen aikana myöhemmin. Jo valitut kirjaimet eivät käänny takaisin.',
  htSwapTitle: 'Vaihtuvat kirjaimet',
  htSwapBody:
    'Joskus kierrosten välissä yksi kirjain korvataan toisella. Näet, mikä kirjain poistui ja mikä tuli tilalle.',
  htLevelsTitle: 'Vaikeustasot',
  htLevelEasy:
    'Samat kaksitoista kirjainta koko pelin ajan, joten ehdit opetella ne ja pitää sanalistaa mielessäsi. Laatat kääntyvät hitaasti ja koko lauta näkyy tarpeeksi kauan, että ehdit valita kirjaimesi rauhassa.',
  htLevelMedium:
    'Silloin tällöin yksi kirjain vaihtuu, jolloin on vaikeampi pitää mielessä sanoja, jotka aioit pelata myöhemmin. Vähemmän aikaa katsoa ja vähemmän aikaa ajatella.',
  htLevelHard:
    'Kolmen kirjaimen sanat eivät enää kelpaa, ja kirjain vaihtuu suunnilleen joka toinen kierros. Lauta ehtii tuskin näkyä ennen kuin se sekoitetaan.',
  htLevelInsane:
    'Kaikki yhtä aikaa ja täydellä vauhdilla. Lauta sekoitetaan lähes heti viimeisen siirron jälkeen.',
  htTouchTitle: 'Kosketusnäyttö',
  htTouchBody:
    'Kosketa käännettyä laattaa ottaaksesi sen kirjaimen. Kosketa mitä tahansa ottamaasi kirjainta palauttaaksesi sen. Hyväksy ja Tyhjennä ovat laudan alla.',

  accountTitle: 'Tilisi',
  menuProfile: 'Profiilini',
  menuGames: 'Pelini',
  menuPublicPage: 'Julkinen sivuni',
  menuSignedInAs: 'Kirjautuneena',
  signIn: 'Kirjaudu sisään',
  signOut: 'Kirjaudu ulos',
  signInTitle: 'Kirjaudu tai luo tili',
  signInLead: 'Tili säilyttää pelisi, kaikilla laitteilla joilla pelaat.',
  signInKeepGame: 'Kirjaudu, niin tämä peli säilyy, ja kaikki tämän jälkeen.',
  continueWithApple: 'Jatka Applella',
  continueWithGoogle: 'Jatka Googlella',
  signInOr: 'tai',
  emailLabel: 'Sähköposti',
  codeLabel: 'Koodi',
  emailMeACode: 'Lähetä minulle koodi',
  working: 'Hetki…',
  sendNewCode: 'Lähetä uusi koodi',
  codeSent: 'Katso sähköpostisi. Koodi on voimassa 10 minuuttia.',
  notNow: 'Ei nyt',
  badEmail: 'Tuo ei näytä sähköpostiosoitteelta.',
  badCode: 'Koodi ei kelvannut. Koodit vanhenevat 10 minuutissa ja toimivat kerran.',
  serverBusy: 'Palvelimeen ei saatu yhteyttä. Yritä hetken päästä.',
  serverDown: 'Palvelimeen ei saatu yhteyttä.',
  offline: 'Ei yhteyttä',
  leaderboardTitle: 'Tulostaulu',
  leaderboardEmpty: 'Tällä taululla ei ole vielä pelejä. Ole ensimmäinen.',
  leaderboardWouldBe: 'Mihin tämä peli sijoittuisi',
  leaderboardThisGame: 'Tämä peli',
  leaderboardRanksHere: 'Mihin tämä peli sijoittuu',
  leaderboardOutside: 'Kärkiviisikon ulkopuolella.',
  tutAccountTitle: 'Säilytä tuloksesi',
  tutAccountBody: 'Tili tallentaa pelisi, vie tuloksesi tulostaululle ja säilyttää profiilisi.',
  ssoCancelled: 'Kirjautuminen peruttiin.',
  ssoExpired: 'Siihen meni liian kauan. Kirjaudu uudelleen.',
  ssoNoUsername: 'Tilin luontia ei saatu valmiiksi. Yritä hetken päästä.',
  ssoFailed: 'Se ei onnistunut. Yritä uudelleen tai käytä sähköpostia.',
  tabProfile: 'Profiili',
  countryAny: 'En kerro',
  findCountry: 'Hae maita',
  usernameLabel: 'Käyttäjänimi',
  bioLabel: 'Esittely',
  countryLabel: 'Maa',
  bioHint: 'Ei linkkejä. {left}/{max}',
  save: 'Tallenna',
  saving: 'Tallennetaan…',
  saved: 'Tallennettu.',
  nameTaken: 'Nimi on jo jollakulla.',
  nameTooShort: 'Vähän pidempi, kiitos.',
  nameTooLong: 'Tuo on liian pitkä.',
  nameBadCharacters: 'Vain kirjaimia, numeroita, väliviivoja ja alaviivoja.',
  nameBadEdges: 'Sen on alettava ja päätyttävä kirjaimeen tai numeroon.',
  nameMixedScripts: 'Valitse yksi aakkosto ja pysy siinä.',
  nameReserved: 'Tuo muistuttaa nimeä, jonka annamme itse. Valitse toinen.',
  nameUnusable: 'Tuota nimeä ei voi käyttää.',
  bioTooLong: 'Esittelyyn mahtuu enintään {max} merkkiä.',
  bioHasLink: 'Linkkejä ei sallita esittelyssä.',
  bioHasControl: 'Siinä on merkkejä, joita esittely ei voi sisältää.',
  bioUnusable: 'Tuota esittelyä ei voi käyttää.',
  gamesHeading: 'Pelit',
  gamesEmpty: 'Täällä ei ole vielä mitään. Kirjautuneena pelatut pelit näkyvät täällä.',
  gamesEmptyShort: 'Täällä ei ole vielä mitään.',
  gamesLoading: 'Luetaan pelejäsi…',
  theirGamesLoading: 'Luetaan hänen pelejään…',
  profileLoading: 'Luetaan profiilia…',
  playerNotFound: 'Täällä ei ole ketään sen nimistä.',
  columnWhen: 'Milloin',
  columnRounds: 'kierrosta',
  columnGame: 'Peli',
  gameLoading: 'Luetaan peliä…',
  gameUnreadable: 'Tätä peliä ei voitu lukea.',
  gameNotRanked: 'Pelattu muokatuilla säännöillä, joten tämä peli ei ole tuloslistalla.',
  detailNotKept: 'Tämän pelin sanoja ja lautoja ei enää säilytetä.',
  openingBoard: 'Aloituslauta',
  roundByRound: 'Kierros kerrallaan',
  roundNothing: 'ei löytynyt mitään',
  changeReplaced: '{letter} vaihdettu ruutuun {slot}',
  changeWildOn: '{letter} jokeri ruudussa {slot}',
  changeWildOff: '{letter} jokeri poistettu ruudusta {slot}',
  keepThisGame: 'Säilytä tämä peli',
  savedToYourGames: 'Tallennettu peleihisi.',

  reportThis: 'Ilmianna',
  reportHeading: 'Ilmianna tämä',
  reportTheName: 'Nimi',
  reportTheBio: 'Esittely',
  reportTheScore: 'Tulos',
  reportWhy: 'Mikä siinä on vikana? Valinnainen.',
  reportSend: 'Lähetä ilmianto',
  reportSent: 'Kiitos. Joku katsoo sen.',
  reportSignIn: 'Kirjaudu sisään ilmiantaaksesi jotain.',
  reportGone: 'Sitä ei ole enää.',
  reportCancel: 'Peruuta',

  deleteAccount: 'Poista tilini',
  deleteAccountWhat:
    'Tämä poistaa profiilisi ja kaikki tallentamasi pelit lopullisesti. Sitä ei voi peruuttaa.',
  deleteAccountAsk: 'Lähetä minulle koodi',
  deleteConfirm: 'Poista lopullisesti',
  deleteGone: 'Tilisi on poistettu. Kiitos pelaamisesta.',
  deleteNoAddress:
    'Tällä tilillä ei ole vahvistettua osoitetta, joten poistoa ei voi tarkistaa. Ota yhteyttä, niin teemme sen käsin.',

  rulesetFacts: 'Sanan pituus {letters} · uusi laatta {seconds} välein',
  plurals: {
    words: { one: '{n} sana', other: '{n} sanaa' },
    rounds: { one: '{n} kierros', other: '{n} kierrosta' },
    flips: { one: '{n} siirto', other: '{n} siirtoa' },
    ticks: { one: '{n} askel', other: '{n} askelta' },
    points: { one: '{n} piste', other: '{n} pistettä' },
    minLetters: { one: 'vähintään {n} kirjain', other: 'vähintään {n} kirjainta' },
    seconds: { one: '{n} sekunnin', other: '{n} sekunnin' },
  },
}
